import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { FaChevronLeft, FaDownload, FaMagic, FaEye } from "react-icons/fa";

// Import Quotation Templates
import ProfessionalModernQuotation from "../Templates/ProfessionalModernQuotation";
import CorporateSleekQuotation from "../Templates/CorporateSleekQuotation";
import MinimalistElegantQuotation from "../Templates/MinimalistElegantQuotation";
import CreativeVibrantQuotation from "../Templates/CreativeVibrantQuotation";
import ClassicBusinessQuotation from "../Templates/ClassicBusinessQuotation";
import IndustrialGridQuotation from "../Templates/IndustrialGridQuotation";

const QuotationPDF = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const quotationData = location.state?.PDFData || {};

  const templates = [
    { id: "modern", name: "Professional Modern", component: ProfessionalModernQuotation, color: "bg-emerald-600" },
    { id: "corporate", name: "Corporate Sleek", component: CorporateSleekQuotation, color: "bg-slate-900" },
    { id: "minimalist", name: "Minimalist Elegant", component: MinimalistElegantQuotation, color: "bg-slate-400" },
    { id: "creative", name: "Creative Vibrant", component: CreativeVibrantQuotation, color: "bg-rose-600" },
    { id: "classic", name: "Classic Business", component: ClassicBusinessQuotation, color: "bg-slate-800" },
    { id: "industrial", name: "Industrial Grid", component: IndustrialGridQuotation, color: "bg-emerald-900" },
  ];

  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownload = async () => {
    const element = printRef.current;
    if (!element) return;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff"
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4"
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Quotation_${quotationData.title || "001"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const SelectedComponent = selectedTemplate.component;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Control Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
          >
            <FaChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tighter uppercase italic">Quotation Preview</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">Choose a professional template</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-xl">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTemplate(t)}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${selectedTemplate.id === t.id
                    ? `${t.color} text-white shadow-lg`
                    : "text-slate-400 hover:text-slate-600"
                  }`}
              >
                {t.name.split(' ')[0]}
              </button>
            ))}
          </div>

          <button
            onClick={handleDownload}
            disabled={isGenerating}
            className="flex items-center gap-3 bg-slate-900 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-800 transition-all disabled:opacity-50 shadow-xl shadow-slate-200"
          >
            {isGenerating ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <FaDownload />
            )}
            {isGenerating ? "Processing..." : "Export PDF"}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar Selector */}
        <div className="w-80 bg-white border-r border-slate-200 p-8 overflow-y-auto hidden lg:block">
          <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-8 flex items-center gap-2">
            <FaMagic className="text-emerald-600" /> Style Gallery
          </h3>
          <div className="space-y-6">
            {templates.map((t) => (
              <div
                key={t.id}
                onClick={() => setSelectedTemplate(t)}
                className={`group cursor-pointer rounded-2xl border-2 transition-all p-4 ${selectedTemplate.id === t.id
                    ? "border-emerald-600 bg-emerald-50/50 shadow-lg shadow-emerald-100"
                    : "border-slate-100 hover:border-slate-200"
                  }`}
              >
                <div className={`w-full aspect-[1/1.41] ${t.color} rounded-lg mb-4 flex items-center justify-center relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-white/10 group-hover:bg-transparent transition-colors"></div>
                  <FaEye className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <h4 className={`text-xs font-black uppercase tracking-widest ${selectedTemplate.id === t.id ? "text-emerald-600" : "text-slate-900"}`}>
                  {t.name}
                </h4>
              </div>
            ))}
          </div>
        </div>

        {/* Preview Area */}
        <div className="flex-1 overflow-y-auto p-12 flex justify-center bg-slate-200/50 scrollbar-hide">
          <div className="relative group shadow-2xl shadow-black/10 origin-top transform scale-[0.85] xl:scale-[0.9] transition-transform">
            <div ref={printRef} className="bg-white shadow-2xl">
              <SelectedComponent data={quotationData} />
            </div>

            {/* Overlay when generating */}
            {isGenerating && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-sm">
                <div className="bg-slate-900 text-white p-8 rounded-3xl flex flex-col items-center gap-4 shadow-2xl">
                  <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                  <div className="text-[10px] font-black uppercase tracking-[0.3em]">Building Masterpiece...</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationPDF;
